import { FC, ComponentProps, useMemo, useState, useEffect, useRef } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import {
  Colors,
  DropDown,
  DropDownItem,
  ItemInfo,
  RenderCallback,
  prepareItemsForDropDown,
  useStyle,
  useThemeValue,
} from '@altermaind/web/ui';
import { getModeTheme } from '@/_shared/styles/state/selectors';
import { SelectItem } from '../types';
import { WrappedInput } from '../WrappedInput';

type Props = Omit<
  ComponentProps<typeof DropDown>,
  'items' | 'setOpen' | 'onSelect' | 'searchble'
> & {
  control: Control<FieldValues>;
  name: string;
  additonalProps?: Record<string, unknown>;
  items: SelectItem[];
  defaultValue?: ComponentProps<typeof Controller>['defaultValue'];
  searchActive?: boolean;
  loading?: boolean;
  hasBorderColor?: boolean;
  backgroundColorHeader?: keyof Colors;
  onItemSelect?: (item: SelectItem) => void;
  alwaysShowLabel?: boolean;
};

export const WrappedDropdown: FC<Props> = ({
  control,
  name,
  additonalProps = {},
  searchActive = false,
  defaultValue,
  loading,
  hasBorderColor = false,
  backgroundColorHeader = undefined,
  onItemSelect,
  alwaysShowLabel = false,
  ...props
}) => {
  const t = useTranslations('inputs');
  const [open, setOpen] = useState<boolean>(false);
  const themeMode = useSelector(getModeTheme());

  const selectFilter = (item: DropDownItem<SelectItem>, searchValue: string) =>
    (item as unknown as DropDownItem<SelectItem>)?.item?.label
      .toUpperCase()
      .includes(searchValue.toUpperCase());

  const items = useMemo(
    () =>
      prepareItemsForDropDown([...props.items], {
        defaultSelected: [],
      }),
    [props.items],
  );

  const contentExtractor: RenderCallback<SelectItem, ItemInfo> = ({ item }) => {
    return {
      text: item.label,
      textColor: 'primary/text',
      checkBox: false,
      disabled: false,
    };
  };

  const searchProp = useMemo(() => {
    const MIN_ITEMS = 1;

    if (items.length < MIN_ITEMS) {
      return undefined;
    }

    return {
      placeholder: t('placeholderSearch'),
      filterCallback: selectFilter,
    };
  }, [items.length, t]);

  const color: keyof Colors = (props?.labelStyle?.color as keyof Colors) || 'primary/text';
  const parsedColor = useStyle({ color });

  const backgroundColorHeaderDefault = useThemeValue('colors', 'secondary/background');

  const ref = useRef(null);

  const clickChecker = (event: MouseEvent) => {
    if (ref.current && !(ref.current as HTMLElement).contains(event.target as HTMLElement)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => clickChecker(e);
    if (ref.current) {
      document.addEventListener('click', handleClick);
    }
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div ref={ref}>
      <Controller
        control={control}
        name={name}
        rules={{ ...additonalProps }}
        defaultValue={defaultValue}
        render={({ field }) => {
          const elements = items.map(item => {
            return {
              ...item,
              selected: field.value?.value === item.item.value,
            };
          });

          if (loading) {
            return (
              <WrappedInput
                name={name}
                control={control}
                value={field?.value?.label || props.placeholder}
                suffixSvg='loading'
                suffix='loading'
                disabled
                loading
                iconStyle={themeMode === 'dark' ? { color: 'white' } : undefined}
                label={props.label || ''}
              />
            );
          }

          return (
            <DropDown
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...field}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...props}
              labelStyle={{ mb: 'xs', color }}
              items={elements}
              label={alwaysShowLabel || field.value ? props.label : undefined}
              placeholder={field?.value?.label || props.placeholder}
              searchable={searchActive ? searchProp : undefined}
              showSvg
              closeOnSelect
              dropDownHeaderStyle={
                {
                  border: hasBorderColor ? `1px solid ${parsedColor.color} !important` : undefined,
                  textStyle: { mr: 'xs' },
                  height: 44,
                  background: backgroundColorHeader || backgroundColorHeaderDefault,
                } as never
              }
              overlay
              zIndex={1000}
              maxHeight={200}
              keyExtractor={(item: SelectItem) => item.value}
              contentExtractor={contentExtractor}
              open={open}
              setOpen={() => setOpen(!open)}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onSelect={value => {
                field.onChange(value.item);
                onItemSelect?.(value.item);
                setOpen(!open);
              }}
            />
          );
        }}
      />
    </div>
  );
};
